import React, { useState, useEffect } from "react";
import { Bar, Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  ArcElement,
} from "chart.js";
import { usePremisesStore, useRegionStore } from "../../store/store";
import { Dropdown } from "../../components/dropdown/Dropdown";
import PermissionWrapper from "../../components/PermissionWrapper/PermissionWrapper";
import { INCIDENT_DASHBOARD } from "../../constants/modules";
import { DashboardData } from "./types/types";
import { fetchDashboardStats } from "./services/service";

ChartJS.register(
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  ArcElement
);

const IMDashboard = () => {
  const [dashboardData, setDashboardData] = useState<DashboardData>({
    categories: [],
    categoryData: [],
    totalIncidents: 0,
    openIncidents: 0,
    pendingIncidents: 0,
    resolvedIncidents: 0,
    rejectedIncidents: 0,
    inReviewIncidents: 0,
    criticalIncidents: 0,
    highIncidents: 0,
    mediumIncidents: 0,
    lowIncidents: 0,
  });
  const [loading, setLoading] = useState(true);
  const [selectedRegion, setSelectedRegion] = useState<string>("");
  const [selectedPremises, setSelectedPremises] = useState<string>("");
  const { fetchPremises } = usePremisesStore();
  const { fetchRegions } = useRegionStore();

  // Initialize data on component mount
  useEffect(() => {
    fetchPremises();
    fetchRegions();
  }, [fetchPremises, fetchRegions]);

  // Reset premises selection when region changes
  useEffect(() => {
    setSelectedPremises("");
  }, [selectedRegion]);

  // Fetch dashboard data with region and premises filters
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);

        const filters: { regionId?: string; premisesId?: string } = {};

        // Check if region is selected and not "All"
        if (selectedRegion && selectedRegion !== "" && selectedRegion !== "All") {
          filters.regionId = selectedRegion;
        }

        // Check if premises is selected and not "All"
        if (selectedPremises && selectedPremises !== "" && selectedPremises !== "All") {
          filters.premisesId = selectedPremises;
        }

        const response = await fetchDashboardStats(filters);

        if (response.success) {
          const data = response.data;
          setDashboardData({
            categories: data.departmentCounts ? Object.keys(data.departmentCounts) : [],
            categoryData: data.departmentCounts ? Object.values(data.departmentCounts) : [],
            totalIncidents: data.totalCount || 0,
            openIncidents: data.statusCounts?.open || 0,
            pendingIncidents: data.statusCounts?.pending || 0,
            resolvedIncidents: data.statusCounts?.resolved || 0,
            rejectedIncidents: data.statusCounts?.rejected || 0,
            inReviewIncidents: data.statusCounts?.inReview || 0,
            criticalIncidents: data.severityCounts?.critical || 0,
            highIncidents: data.severityCounts?.high || 0,
            mediumIncidents: data.severityCounts?.medium || 0,
            lowIncidents: data.severityCounts?.low || 0,
          });
        }
      } catch (error) {
        console.error('Dashboard fetch error:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [selectedRegion, selectedPremises]);

  const barData = {
    labels: dashboardData.categories.length > 0 ? dashboardData.categories : [
      "Fire Safety",
      "Category-2",
      "Category-3",
      "Category-4",
      "Category-5",
      "Category-6",
      "Category-7",
      "Category-8",
      "Category-9",
    ],
    datasets: [
      {
        label: "Incidents",
        data: dashboardData.categoryData.length > 0 ? dashboardData.categoryData : [55, 53, 19, 36, 29, 49, 45, 5, 56, 27],
        backgroundColor: "#3B82F6",
        borderRadius: 8,
      },
    ],
  };

  const barOptions = {
    responsive: true,
    plugins: {
      legend: {
        display: false,
      },
    },
  };

  const incidentStats = [
    {
      title: "Total Incident",
      value: dashboardData.totalIncidents || 0,
      iconBg: "bg-indigo-100"
    },
    {
      title: "Open",
      value: dashboardData.openIncidents || 0,
      iconBg: "bg-blue-100"
    },
    {
      title: "Pending Incident",
      value: dashboardData.pendingIncidents || 0,
      iconBg: "bg-orange-100"
    },
    {
      title: "Resolved Incident",
      value: dashboardData.resolvedIncidents || 0,
      iconBg: "bg-teal-100"
    },
  ];

  // const activeIncidents = dashboardData.openIncidents + dashboardData.pendingIncidents + dashboardData.inReviewIncidents;
  const activeIncidents = dashboardData.pendingIncidents;
  const activeSeverityTotal = activeIncidents || 1; // Avoid division by zero

  const severityStats = [
    {
      title: "Critical Incidents",
      percent: `${Math.round((dashboardData.criticalIncidents / activeSeverityTotal) * 100)}%`,
      color: "#D32F2F", // red-700
      value: Math.round((dashboardData.criticalIncidents / activeSeverityTotal) * 100),
    },
    {
      title: "High Incidents",
      percent: `${Math.round((dashboardData.highIncidents / activeSeverityTotal) * 100)}%`,
      color: "#F57C00", // orange-500
      value: Math.round((dashboardData.highIncidents / activeSeverityTotal) * 100),
    },
    {
      title: "Medium Incidents",
      percent: `${Math.round((dashboardData.mediumIncidents / activeSeverityTotal) * 100)}%`,
      color: "#FBC02D", // yellow-500
      value: Math.round((dashboardData.mediumIncidents / activeSeverityTotal) * 100),
    },
    {
      title: "Low Incidents",
      percent: `${Math.round((dashboardData.lowIncidents / activeSeverityTotal) * 100)}%`,
      color: "#8D6E63", // brown-500
      value: Math.round((dashboardData.lowIncidents / activeSeverityTotal) * 100),
    },
  ];

  const getDonutData = (value: number, color: string) => {
    return {
      datasets: [
        {
          data: [value, 100 - value],
          backgroundColor: [color, "#E5E7EB"],
          borderWidth: 0,
          cutout: "75%",
        },
      ],
    };
  };

  const donutOptions = {
    responsive: true,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: false,
      },
    },
    rotation: -90,
    circumference: 360,
  };

  if (loading) {
    return <div className="mt-16 px-2 py-4 text-center">Loading dashboard data...</div>;
  }

  return (
    <PermissionWrapper module={INCIDENT_DASHBOARD}>
      <div className="mt-16 px-2 py-4 space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-semibold">Dashboard</h1>
          <div className="flex items-center gap-4">
            <Dropdown
              selectedValue={selectedRegion}
              onChange={setSelectedRegion}
              type="region"
            />
            <Dropdown
              selectedValue={selectedPremises}
              onChange={setSelectedPremises}
              type="premises"
              selectedRegion={selectedRegion}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {incidentStats.map((stat, index) => (
            <div
              key={index}
              className="bg-white p-4 rounded-xl shadow-sm flex justify-between items-center"
            >
              <div>
                <p className="text-sm text-black font-semibold">{stat.title}</p>
                <h2 className="text-2xl font-bold">{stat.value}</h2>
              </div>
              <div className={`w-10 h-10 rounded-lg ${stat.iconBg}`}></div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="bg-white rounded-xl p-4 shadow-sm col-span-2">
            <h2 className="text-lg font-semibold mb-4">
              Overall Categories Status
            </h2>
            <Bar data={barData} options={barOptions} />
          </div>

          <div className="space-y-4">
            {severityStats.map((item, idx) => (
              <div
                key={idx}
                className="bg-white p-4 rounded-xl shadow-sm flex justify-between items-center"
              >
                <div>
                  <p className="text-sm text-black font-semibold">{item.title}</p>
                  <h2 className="text-xl font-semibold">{item.percent}</h2>
                </div>
                <div className="w-16 h-16 relative">
                  <Doughnut
                    data={getDonutData(item.value, item.color)}
                    options={donutOptions}
                  />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-xs font-semibold">{item.percent}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </PermissionWrapper>
  );
};

export default IMDashboard;
